import { useEffect, useRef } from "react";
import { RecaptchaVerifier } from "firebase/auth";
import { auth } from "../firebase";

// Module-level cache — survives React component unmount/remount cycles.
// Without this, closing and reopening the Login modal (or React Strict Mode
// double-mounting) would try to create a new RecaptchaVerifier on a container
// that already has one, triggering "reCAPTCHA has already been rendered in
// this element".
const verifierCache = new Map<string, RecaptchaVerifier>();

export function useRecaptchaVerifier(containerId: string) {
  const verifierRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Reuse a previously created verifier — avoids re-render errors
    const cached = verifierCache.get(containerId);
    if (cached) {
      verifierRef.current = cached;
      window.recaptchaVerifier = cached;
      return;
    }

    // Stale widget left in the DOM from a previous mount that was not
    // properly torn down (e.g. HMR, Strict Mode, or fast navigation).
    // Nuke it before creating a fresh verifier.
    const staleWidget = container.querySelector(
      'iframe[src*="recaptcha"], .g-recaptcha, div[style*="recaptcha"]',
    );
    if (staleWidget || window.recaptchaVerifier) {
      container.innerHTML = "";
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch {
          // clear() may throw if the widget is already torn down
        }
        window.recaptchaVerifier = undefined;
      }
    }

    try {
      // The RecaptchaVerifier constructor renders the widget automatically
      // when given a container ID — no need for an explicit .render() call.
      const verifier = new RecaptchaVerifier(auth, containerId, {
        size: "invisible",
        callback: () => {},
      });
      verifierRef.current = verifier;
      window.recaptchaVerifier = verifier;
      verifierCache.set(containerId, verifier);
    } catch (err) {
      console.debug("Error creating RecaptchaVerifier:", err);
    }

    // Intentionally do NOT clear the verifier on unmount.  The container lives
    // in index.html for the full lifetime of the app; the verifier should
    // persist across page navigations and modal open/close so that re-mounting
    // components always hit the cache above.
    return () => {
      verifierRef.current = null;
    };
  }, [containerId]);

  return verifierRef;
}
